import {mousePosition,tmpGroup} from "../../main";
import {Mesh, PlaneGeometry, Vector3, Box3} from "three";
import * as Globals from "../../globals";
import {previewMaterialRed, lineMaterial} from "../../constants/materialConstants";
import {degrees90} from "../../constants/mathConstants";
import {highlightedObject} from "../../utils/highlighting";
import {toFixedFloat} from "../../utils/utils";
import {LineGeometry} from "../../models/baseElements/line";
import * as BGU from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export class MeasurementPointer {
    constructor() {
        this.resetPointers();
    }

    onMeasurementClick() {
        if (! highlightedObject) {
            return;
        }
        if (this.secondPreviewMesh) {
            this.secondPosition = this.secondPreviewMesh.position.clone();
            this.secondSelectedObj = highlightedObject;
        } else {
            this.firstPosition = this.firstPreviewMesh.position.clone();
            this.firstSelectedObj = highlightedObject;
            this.createSecondPreview();
        }
    }

    resetPointers() {
        this.firstPreviewMesh = null;
        this.secondPreviewMesh = null;
        this.firstPosition = null;
        this.secondPosition = null;
        this.secondSelectedObj = null;
        this.firstSelectedObj = null;
        this.createFirstPreview();
    }

    //First preview step
    createFirstPreview() {
        this.firstPreviewMesh = this.createPreviewMesh();
        this.adjustPreview();
        tmpGroup.addWithUuid(this.firstPreviewMesh);
    }

    createSecondPreview() {
        this.secondPreviewMesh = this.createPreviewMesh();
        this.adjustPreview();
        tmpGroup.addWithUuid(this.secondPreviewMesh);
    }

    createPreviewMesh() {
        let lineLength = 0.2;
        let geometry = new PlaneGeometry(0.3, 0.02);
        let geometry2 = new PlaneGeometry(0.3, 0.02);
        geometry2.rotateZ(Math.PI / 2);
        geometry2.rotateX(Math.PI / 2);
        geometry.rotateX(Math.PI / 2);

        let merged = BGU.mergeBufferGeometries([geometry, geometry2]);

        return new Mesh(merged, lineMaterial);

        return previewMesh;
    }

    adjustPreview() {
        let previewMesh = this.secondPreviewMesh || this.firstPreviewMesh;
        let snapPoint = this.snapPreview();

        if (snapPoint && snapPoint.distance <= 0.5) {
            previewMesh.position.copy(snapPoint.point);
        } else {
            previewMesh.position.copy(mousePosition);
        }
        previewMesh.position.y = Globals.defaultPreviewHeight;
    }

    snapPreview() {
        if (highlightedObject) {
            return this.snapPointToBoundsVertex(mousePosition, highlightedObject);
        }
        return null;
    }

    snapPointToBoundsVertex(startPoint, mesh) {
        startPoint.setY(Globals.defaultPreviewHeight);
        //Find the closest vertex from bounds to startPoint(mouse position)
        let nearestPoint, nearestDistance;
        let points = this.getPointsFromBoundingBox(mesh);

        for (let i = 0; i < points.length; i++) {
            if (i === 0) {
                nearestPoint = points[i];
                nearestDistance = startPoint.distanceTo(points[i]);
            }

            let distance = startPoint.distanceTo(points[i]);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestPoint = points[i];
            }
        }

        return {point:nearestPoint, distance:nearestDistance};
    }

    getPointsFromBoundingBox(mesh) {
        let bounds = new Box3().setFromObject(mesh);
        let position = (mesh.userData.sceneMatrix?.position || mesh.position).clone();
        let isWall = mesh.name == 'wallGroup';
        let points = [];
        let yPos = Globals.defaultPreviewHeight;
        position.setY(yPos);

        if (Math.abs(bounds.max.z - bounds.min.z) > Math.abs(bounds.max.x - bounds.min.x)) {
            //vertical, sync x pos
            let addX = isWall ? (bounds.max.x - bounds.min.x)/2 : 0; 
            points = [
                new Vector3(position.x - addX, yPos, bounds.max.z),
                new Vector3(position.x - addX, yPos, bounds.min.z)
            ];
            position.setX(position.x - addX);
        } else {
            let addZ = isWall ? (bounds.max.z - bounds.min.z)/2 : 0; 
            //horizontal, sync z pos
            points = [
                new Vector3(bounds.max.x, yPos, position.z + addZ),
                new Vector3(bounds.min.x, yPos, position.z + addZ)
            ];
            position.setZ(position.z + addZ);
        }
        
        points.push(position);

        return points;
    }
}